
import { useEffect, useRef, useState } from "react";
import { Avatar, Button, Dialog, Tooltip, Slider } from "@mui/material";
import api from "../services/api";
import PropTypes from 'prop-types';
import { useSelector } from "react-redux";
import MessageAlert from "../messages/messageAlert";
import AvatarEditor from "react-avatar-editor";
import BottonLeft from "./buttonLeft";

const tamanhoImage = 270;
const MAX_IMAGE_SIZE_MB = 8;

// Converted to Tailwind classes; useStyles removed

function SimpleDialog(props) {
    const { onClose, open, submitEdit, setEdit, edit, Foto, openLoad, messageSuccess, messageError, editorRef, scale, setScale, previewSrc, inputId } = props;

    const handleClose = () => { onClose(false); };

    return (
        <Dialog open={open} onClose={handleClose} fullWidth disablePortal>
            <div style={{ lineHeight: 1.5, padding: 10 }} align="center" >

                {messageSuccess && <MessageAlert type={'success'} message={messageSuccess} />}
                {messageError && <MessageAlert type={'error'} message={messageError} />}

                <div className="tw-mb-2.5 tw-flex tw-justify-center tw-items-center">
                    <div>
                        {edit ?
                            <Avatar className="tw-w-[270px] tw-h-[270px] tw-rounded-[5px] tw-transition-opacity hover:tw-opacity-50" variant="square" src={previewSrc} title="Clique para carregar uma imagem" />
                            :
                            <Avatar className="tw-w-[270px] tw-h-[270px] tw-rounded-[5px]" variant="square" src={Foto} title="Clique para carregar uma imagem" />
                        }
                    </div>
                    <input accept="image/png, image/jpg, image/jpeg" type="file" name="foto" id={inputId} className="tw-hidden" onChange={(e) => setEdit(e.target.files[0])} />
                </div>

                {edit && (
                    <>
                        <div className="tw-flex tw-flex-col tw-items-center">
                            <AvatarEditor
                                ref={editorRef}
                                image={edit}
                                width={230}
                                height={230}
                                border={10}
                                borderRadius={0}
                                scale={scale}
                            />

                            <div className="tw-w-[240px] tw-mt-2">
                                <Slider
                                    value={scale}
                                    min={1}
                                    max={3}
                                    step={0.1}
                                    onChange={(_, value) => setScale(value)}
                                    valueLabelDisplay="auto"
                                />
                            </div>
                        </div>
                    </>
                )}

                {edit ?
                    <BottonLeft
                        title={'Salvar'}
                        funcao={submitEdit}
                        disabled={openLoad}
                        load={openLoad}
                    >
                        <Button onClick={() => setEdit('')} color="error" >cancelar</Button>

                    </BottonLeft>
                    :
                    <Button autoFocus className="tw-mr-2" size="small" disabled={openLoad}>
                        <label htmlFor={inputId} className="tw-cursor-pointer tw-text-center"> Carregar imagem</label>
                    </Button>
                }

            </div>
        </Dialog>
    );
}

SimpleDialog.propTypes = {
    onClose: PropTypes.func.isRequired,
    open: PropTypes.bool.isRequired,
};

export default function FotoEdit({ dadosPessoal, openEditFoto, setOpenEditFoto, viewEdit }) {
    const editorRef = useRef(null);
    const [Foto, setFoto] = useState(dadosPessoal?.fotoURL)
    const [edit, setEdit] = useState('');
    const [previewSrc, setPreviewSrc] = useState('');
    const [scale, setScale] = useState(1);
    const [openLoad, setOpenLoad] = useState(false)
    const [messageSuccess, setMessageSuccess] = useState('')
    const [messageError, setMessageError] = useState('')
    const user = useSelector(state => state.account.user);
    const inputId = `foto-${dadosPessoal?._id || 'perfil'}`;

    useEffect(() => {
        if (!edit) {
            setPreviewSrc('');
            return;
        }

        const filePreview = URL.createObjectURL(edit);
        setPreviewSrc(filePreview);

        return () => URL.revokeObjectURL(filePreview);
    }, [edit]);

    useEffect(() => {
        if (dadosPessoal?.fotoURL) {
            setFoto(dadosPessoal.fotoURL);
        }
    }, [dadosPessoal?.fotoURL]);

    const handleSetEdit = (file) => {
        setMessageSuccess('');
        setMessageError('');

        if (!file) {
            setEdit('');
            setScale(1);
            return;
        }

        const allowedTypes = ['image/png', 'image/jpg', 'image/jpeg'];
        const maxSizeBytes = MAX_IMAGE_SIZE_MB * 1024 * 1024;

        if (!allowedTypes.includes(file.type)) {
            setMessageError('Formato inválido. Use PNG ou JPEG.');
            setEdit('');
            return;
        }

        if (file.size > maxSizeBytes) {
            setMessageError(`A imagem deve ter no máximo ${MAX_IMAGE_SIZE_MB}MB.`);
            setEdit('');
            return;
        }

        setScale(1);
        setEdit(file);
    };

    const getCroppedImageFile = () => {
        const editor = editorRef.current;
        if (!editor) return null;

        const canvas = editor.getImageScaledToCanvas();
        return new Promise((resolve) => {
            canvas.toBlob((blob) => {
                if (!blob) {
                    resolve(null);
                    return;
                }

                const croppedFile = new File([blob], `foto-${Date.now()}.jpg`, { type: 'image/jpeg' });
                resolve(croppedFile);
            }, 'image/jpeg', 0.9);
        });
    };

    const handleClose = () => {
        setOpenEditFoto && setOpenEditFoto(false);
        setEdit('')
        setScale(1);
        setMessageError('');
        setMessageSuccess('');
    };

    const submitEdit = async () => {
        if (!edit) {
            setMessageError('Selecione uma imagem para continuar.');
            return;
        }

        setOpenLoad(true)
        setMessageSuccess('')
        setMessageError('')

        const imagemFinal = await getCroppedImageFile();
        const formData = new FormData();
        formData.append('foto', imagemFinal || edit);
        formData.append('dadosPessoaisId', dadosPessoal._id);
        formData.append('userId', user?._id ?? '');

        try {
            const res = await api.patch('/dados-pessoais/change-foto', formData);
            setFoto(res.data.dadosPessoais.fotoURL)
            setEdit('')
            setScale(1);
            setMessageSuccess('Foto atualizada com sucesso.')
        } catch (err) {
            setMessageError(err?.response?.data?.message || 'Não foi possivel realizar esta ação.')
        } finally {
            setOpenLoad(false)
        }
    }

    return (
        <>
            <SimpleDialog
                Foto={Foto}
                open={openEditFoto}
                openLoad={openLoad}
                onClose={handleClose}
                submitEdit={submitEdit}
                edit={edit}
                setEdit={handleSetEdit}
                messageSuccess={messageSuccess}
                messageError={messageError}
                editorRef={editorRef}
                scale={scale}
                setScale={setScale}
                previewSrc={previewSrc}
                inputId={inputId}
            />

            {/* user?.isAdmin &&  */}
            <Tooltip title={dadosPessoal?.nome}>
                <div onClick={() => setOpenEditFoto && setOpenEditFoto(true)} className="tw-mb-2.5 tw-flex tw-justify-center">
                    {viewEdit === true ? '' : edit
                        ?
                        <Avatar alt={dadosPessoal?.nome} className="tw-w-[270px] tw-h-[270px] tw-rounded-[5px] tw-transition-opacity hover:tw-opacity-50" variant="square" src={previewSrc} title="Clique para carregar uma imagem" />
                        :
                        <Avatar alt={dadosPessoal?.nome} className="tw-w-[270px] tw-h-[270px] tw-rounded-[5px]" variant="square" src={Foto} title={user?.tipo === 'Admin' ? "Clique para carregar uma imagem" : ''} />
                    }
                </div>
            </Tooltip>
        </>
    )
}